import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShieldCheckIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  BuildingOffice2Icon,
  UserGroupIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowPathIcon,
  XMarkIcon,
  CalendarDaysIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { complianceService } from '../services/compliance.service';
import type {
  ComplianceSummaryResponseContract,
  StatusProcuracaoCompliance,
  TipoLaudoRegulatorio,
} from '@sstexames/shared-types';
import { useToast, ToastContainer } from '../components/Toast';
import { useEmpresa } from '../contexts/EmpresaContext';
import { formatCNPJ } from '../utils/formatters';

export default function ComplianceRegulamentacao() {
  const { empresa } = useEmpresa();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<'procuracao' | 'contrato' | 'laudos'>('procuracao');
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Estados do Modal de Renovação
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [vigenciaMeses, setVigenciaMeses] = useState<number>(24);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [downloadingPath, setDownloadingPath] = useState<string | null>(null);

  const handleDownloadDocumento = async (path?: string | null, nomeArquivo?: string) => {
    if (!path || downloadingPath) return;
    try {
      setDownloadingPath(path);
      await complianceService.downloadDocumento(path, nomeArquivo);
      toast.success('Download concluído com sucesso!');
    } catch {
      toast.error('Erro ao baixar documento.');
    } finally {
      setDownloadingPath(null);
    }
  };

  // Consulta do Resumo Geral
  const { data: summary, isLoading, isError, refetch } = useQuery<ComplianceSummaryResponseContract>({
    queryKey: ['customer-compliance-summary', empresa?.id],
    queryFn: () => complianceService.getSummary(),
    enabled: !!empresa?.id,
  });

  // Mutação para envio/renovação de Procuração
  const renewMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile) throw new Error('Selecione um arquivo para enviar');
      return complianceService.uploadProcuracao(selectedFile, vigenciaMeses, (pct) => {
        setUploadProgress(pct);
      });
    },
    onSuccess: () => {
      toast.success('Procuração eletrônica enviada com sucesso!');
      setShowRenewModal(false);
      setSelectedFile(null);
      setUploadProgress(0);
      queryClient.invalidateQueries({ queryKey: ['customer-compliance-summary'] });
    },
    onError: (err: unknown) => {
      const errObj = err as { message?: string; response?: { data?: { message?: string } } };
      const msg = errObj.response?.data?.message || errObj.message || 'Erro ao enviar procuração';
      toast.error(msg);
      setUploadProgress(0);
    },
  });

  const procuracao = summary?.procuracao;
  const contrato = summary?.contrato;
  const laudos = useMemo(() => summary?.laudos || [], [summary?.laudos]);
  const laudosHistorico = summary?.laudos_historico || [];
  const procuracoesHistorico = summary?.procuracoes_historico || [];

  // Extrair unidades de negócio / postos se existirem laudos segmentados
  const unidadesPostos = useMemo(() => {
    const postosMap = new Map<string, { id: string; nome: string }>();
    (laudos || []).forEach((l) => {
      if (l.is_posto_servico) {
        const id = String(l.posto_trabalho_id || l.setor_id || l.nome_unidade_negocio || l.id);
        postosMap.set(id, {
          id,
          nome: l.nome_unidade_negocio || (l.posto_trabalho_id ? `Posto #${l.posto_trabalho_id}` : l.setor_id ? `Setor Prestador #${l.setor_id}` : `Unidade #${l.id}`),
        });
      }
    });
    return Array.from(postosMap.values());
  }, [laudos]);

  const temPostos = unidadesPostos.length > 0;
  const [unidadeSelecionadaId, setUnidadeSelecionadaId] = useState<string>('principal');

  const laudosFiltrados = useMemo(() => {
    if (!temPostos || unidadeSelecionadaId === 'principal') {
      return (laudos || []).filter((l) => !l.is_posto_servico);
    }
    return (laudos || []).filter(
      (l) => l.is_posto_servico && String(l.posto_trabalho_id || l.setor_id || l.nome_unidade_negocio || l.id) === unidadeSelecionadaId,
    );
  }, [laudos, temPostos, unidadeSelecionadaId]);

  // Helper para formatar data BR
  const formatDateBR = (dateStr?: string | null) => {
    if (!dateStr) return '—';
    try {
      const clean = dateStr.split('T')[0];
      const parts = clean.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  // Badges de Status da Procuração
  const renderStatusBadgeProcuracao = (status?: StatusProcuracaoCompliance, dias?: number | null) => {
    switch (status) {
      case 'vigente':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
            <CheckCircleIcon className="w-4 h-4 text-emerald-600" />
            Vigente {dias !== null && dias !== undefined && `(${dias} dias restantes)`}
          </span>
        );
      case 'a_vencer':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200 animate-pulse">
            <ExclamationTriangleIcon className="w-4 h-4 text-amber-600" />
            A Vencer ({dias} dias restantes)
          </span>
        );
      case 'vencida':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-200">
            <XCircleIcon className="w-4 h-4 text-rose-600" />
            Vencida
          </span>
        );
      case 'pendente':
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-300">
            <ClockIcon className="w-4 h-4 text-gray-500" />
            Pendente de Envio
          </span>
        );
    }
  };

  const getLaudoNomeCompleto = (tipo: TipoLaudoRegulatorio) => {
    switch (tipo) {
      case 'pgr':
        return 'PGR • Programa de Gerenciamento de Riscos (NR-01)';
      case 'pcmso':
        return 'PCMSO • Saúde Ocupacional (NR-07)';
      case 'ltcat':
        return 'LTCAT • Condições Ambientais do Trabalho (IN 128)';
      case 'unificado_pgr_pcmso_ltcat':
        return 'Dossiê Unificado de SST (PGR + PCMSO + LTCAT)';
      default:
        return String(tipo || '').toUpperCase();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/60 pb-16">
      {/* Header Principal */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-emerald-50 rounded-xl border border-emerald-100">
                  <ShieldCheckIcon className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                    Compliance & Regulamentação
                  </h1>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Central corporativa de outorgas e-CAC, instrumentos contratuais e laudos regulatórios de SST
                  </p>
                </div>
              </div>
            </div>

            {/* Quick KPIs / Badges */}
            <div className="flex items-center gap-3">
              <div className="bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">Empresa Ativa:</span>
                <span className="text-xs font-bold text-gray-900 truncate max-w-[200px]">
                  {procuracao?.empresa_nome || 'Minha Empresa'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => refetch()}
                className="p-2 text-gray-500 hover:text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl transition-colors shadow-2xs"
                title="Atualizar dados"
              >
                <ArrowPathIcon className={`w-4 h-4 ${isLoading ? 'animate-spin text-emerald-600' : ''}`} />
              </button>
            </div>
          </div>

          {/* Abas de Navegação */}
          <div className="flex gap-2 mt-6 border-b border-gray-200 -mb-px">
            <button
              type="button"
              onClick={() => setActiveTab('procuracao')}
              className={`flex items-center gap-2 pb-3 px-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'procuracao'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ShieldCheckIcon className="w-4 h-4" />
              Procurações Eletrônicas (e-CAC)
              {procuracao?.status === 'vigente' && (
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
              )}
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('contrato')}
              className={`flex items-center gap-2 pb-3 px-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'contrato'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <DocumentCheckIcon className="w-4 h-4" />
              Meu Contrato Corporativo
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('laudos')}
              className={`flex items-center gap-2 pb-3 px-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'laudos'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <DocumentTextIcon className="w-4 h-4" />
              Laudos Regulatórios (PGR, PCMSO, LTCAT)
              <span className="text-[11px] bg-slate-100 text-slate-700 font-bold px-1.5 py-0.5 rounded-full">
                {laudos.length}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Conteúdo das Abas */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {isLoading ? (
          <div className="bg-white rounded-2xl p-12 border border-gray-200 text-center shadow-xs">
            <ArrowPathIcon className="w-8 h-8 text-emerald-600 animate-spin mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-600">Carregando governança de compliance...</p>
          </div>
        ) : isError ? (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-center text-rose-700">
            <XCircleIcon className="w-8 h-8 mx-auto mb-2 text-rose-600" />
            <p className="font-semibold text-sm">Não foi possível carregar os dados de Compliance.</p>
            <p className="text-xs text-rose-600 mt-1">Por favor, recarregue a página ou tente novamente em instantes.</p>
          </div>
        ) : (
          <>
            {/* ================= ABA 1: PROCURAÇÕES ================= */}
            {activeTab === 'procuracao' && (
              <div className="space-y-6">
                {/* Card Principal da Procuração Ativa */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-xs relative overflow-hidden">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                    <div className="space-y-3 flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h2 className="text-lg font-bold text-gray-900">
                          Procuração Eletrônica e-CAC RFB (Outorga eSocial)
                        </h2>
                        {procuracao?.versao && (
                          <span className="text-xs bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold px-2.5 py-0.5 rounded-full">
                            Versão v{procuracao.versao}
                          </span>
                        )}
                        {renderStatusBadgeProcuracao(procuracao?.status, procuracao?.dias_restantes)}
                        {procuracao?.is_unificada && (
                          <span className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 font-medium px-2.5 py-0.5 rounded-full">
                            Vínculo Unificado de Holding
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-gray-600 leading-relaxed max-w-3xl">
                        A procuração eletrônica confere os poderes legais necessários para que nossa equipe técnica de Medicina e Segurança do Trabalho realize a transmissão oficial dos eventos de SST ao eSocial (S-2210 - CAT, S-2220 - Monitoramento Biológico e S-2240 - Condições Ambientais).
                      </p>

                      {/* Métricas / Datas de Vigência e Outorgado */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-3">
                        <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
                          <span className="text-[11px] text-gray-500 font-medium block">Outorgado Concedido</span>
                          <span className="text-xs font-bold text-gray-900 mt-0.5 block truncate" title={procuracao?.razao_social_outorgado || 'SST Exames'}>
                            {procuracao?.razao_social_outorgado || 'SST Exames'}
                          </span>
                          {procuracao?.cnpj_outorgado && (
                            <span className="text-[10px] text-gray-500 font-mono block mt-0.5">
                              CNPJ: {formatCNPJ(procuracao.cnpj_outorgado)}
                            </span>
                          )}
                        </div>
                        <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
                          <span className="text-[11px] text-gray-500 font-medium block">Data de Outorga / Início</span>
                          <span className="text-sm font-bold text-gray-900 mt-0.5 block">
                            {formatDateBR(procuracao?.data_emissao)}
                          </span>
                        </div>
                        <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
                          <span className="text-[11px] text-gray-500 font-medium block">Validade / Fim da Vigência</span>
                          <span className="text-sm font-bold text-gray-900 mt-0.5 block">
                            {formatDateBR(procuracao?.data_validade)}
                          </span>
                        </div>
                        <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
                          <span className="text-[11px] text-gray-500 font-medium block">Contagem Regressiva</span>
                          <span className={`text-sm font-bold mt-0.5 block ${
                            procuracao?.dias_restantes && procuracao.dias_restantes <= 30
                              ? 'text-amber-600'
                              : 'text-emerald-700'
                          }`}>
                            {procuracao?.dias_restantes !== null && procuracao?.dias_restantes !== undefined
                              ? `${procuracao.dias_restantes} dias para renovação`
                              : 'Indeterminado / Sem data'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Ações Diretas */}
                    <div className="flex flex-col sm:flex-row md:flex-col gap-2.5 shrink-0">
                      {procuracao?.url ? (
                        <button
                          type="button"
                          onClick={() => handleDownloadDocumento(procuracao.url, procuracao.nome_arquivo || 'Procuracao_eCAC.pdf')}
                          disabled={downloadingPath === procuracao.url}
                          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl shadow-xs transition-colors cursor-pointer"
                        >
                          <ArrowDownTrayIcon className="w-4 h-4" />
                          {downloadingPath === procuracao.url ? 'Baixando...' : 'Baixar Procuração Ativa'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled
                          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-400 border border-gray-200 text-xs font-semibold rounded-xl cursor-not-allowed"
                          title="Nenhum arquivo ativo anexado"
                        >
                          <ArrowDownTrayIcon className="w-4 h-4" />
                          Documento Indisponível
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setShowRenewModal(true)}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white hover:bg-slate-50 text-gray-800 border border-gray-300 text-xs font-semibold rounded-xl shadow-2xs transition-colors"
                      >
                        <ArrowUpTrayIcon className="w-4 h-4 text-emerald-600" />
                        Renovar / Enviar Nova Procuração
                      </button>
                    </div>
                  </div>
                </div>

                {/* Histórico Colapsável de Procurações Anteriores */}
                <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-xs">
                  <button
                    type="button"
                    onClick={() => setShowHistory(!showHistory)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-2">
                      <ClockIcon className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-bold text-gray-900">
                        Histórico de Procurações Anteriores ({procuracoesHistorico.length})
                      </span>
                    </div>
                    {showHistory ? (
                      <ChevronUpIcon className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                    )}
                  </button>

                  {showHistory && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      {procuracoesHistorico.length === 0 ? (
                        <p className="text-xs text-gray-500 py-3 text-center">
                          Nenhuma procuração anterior registrada no histórico.
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs text-left">
                            <thead className="bg-slate-50 text-gray-600 font-semibold border-b border-gray-200">
                              <tr>
                                <th className="py-2.5 px-3">Versão</th>
                                <th className="py-2.5 px-3">Outorgado</th>
                                <th className="py-2.5 px-3">Data de Envio</th>
                                <th className="py-2.5 px-3">Vigência Até</th>
                                <th className="py-2.5 px-3">Status</th>
                                <th className="py-2.5 px-3">Arquivo</th>
                                <th className="py-2.5 px-3 text-right">Ação</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {procuracoesHistorico.map((item, idx) => (
                                <tr key={item.id || idx} className="hover:bg-slate-50/50">
                                  <td className="py-2.5 px-3 text-gray-900 font-bold">
                                    v{item.versao || 1}
                                  </td>
                                  <td className="py-2.5 px-3 text-gray-800 font-medium">
                                    <div className="truncate max-w-[180px]" title={item.razao_social_outorgado || 'SST Exames'}>
                                      {item.razao_social_outorgado || 'SST Exames'}
                                    </div>
                                    {item.cnpj_outorgado && (
                                      <span className="text-[10px] text-gray-500 font-mono block">
                                        {formatCNPJ(item.cnpj_outorgado)}
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-2.5 px-3 text-gray-900 font-medium">
                                    {formatDateBR(item.data_emissao)}
                                  </td>
                                  <td className="py-2.5 px-3 text-gray-600">
                                    {formatDateBR(item.data_validade)}
                                  </td>
                                  <td className="py-2.5 px-3">
                                    {renderStatusBadgeProcuracao(item.status, item.dias_restantes)}
                                  </td>
                                  <td className="py-2.5 px-3 text-gray-500 truncate max-w-[160px]">
                                    {item.nome_arquivo || 'Procuração.pdf'}
                                  </td>
                                  <td className="py-2.5 px-3 text-right">
                                    {item.url ? (
                                      <button
                                        type="button"
                                        onClick={() => handleDownloadDocumento(item.url, item.nome_arquivo || 'Procuracao.pdf')}
                                        disabled={downloadingPath === item.url}
                                        className="text-emerald-700 hover:text-emerald-800 font-semibold hover:underline cursor-pointer"
                                      >
                                        {downloadingPath === item.url ? 'Baixando...' : 'Baixar PDF'}
                                      </button>
                                    ) : (
                                      <span className="text-gray-400">Indisponível</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ================= ABA 2: MEU CONTRATO ================= */}
            {activeTab === 'contrato' && (
              <div className="space-y-6">
                <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-xs">
                  {contrato ? (
                    <div className="space-y-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-gray-100">
                        <div>
                          <div className="flex items-center gap-2.5">
                            <h2 className="text-lg font-bold text-gray-900">
                              {contrato.titulo || 'Contrato de Prestação de Serviços SST'}
                            </h2>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase ${
                              contrato.status === 'ativo'
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-rose-100 text-rose-800'
                            }`}>
                              {contrato.status}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Instrumento jurídico que rege o fornecimento de exames ocupacionais, laudos e gestão de saúde do trabalho.
                          </p>
                        </div>

                        {/* Botão de Download da Minuta / Contrato */}
                        {contrato.minuta_url ? (
                          <button
                            type="button"
                            onClick={() => handleDownloadDocumento(contrato.minuta_url, contrato.minuta_nome || 'Contrato.pdf')}
                            disabled={downloadingPath === contrato.minuta_url}
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl shadow-xs transition-colors shrink-0 cursor-pointer"
                          >
                            <ArrowDownTrayIcon className="w-4 h-4" />
                            {downloadingPath === contrato.minuta_url ? 'Baixando...' : 'Baixar Minuta Contratual (PDF)'}
                          </button>
                        ) : (
                          <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-400 border border-gray-200 text-xs font-semibold rounded-xl cursor-not-allowed shrink-0" title="Minuta em fase de homologação">
                            <ArrowDownTrayIcon className="w-4 h-4" />
                            Contrato Indisponível no Momento
                          </div>
                        )}
                      </div>

                      {/* Grade com Especificações Contratuais */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                          <div className="flex items-center gap-2 text-gray-500 mb-1">
                            <CalendarDaysIcon className="w-4 h-4 text-emerald-600" />
                            <span className="text-xs font-medium">Início da Vigência</span>
                          </div>
                          <span className="text-sm font-bold text-gray-900 block">
                            {formatDateBR(contrato.data_inicio)}
                          </span>
                        </div>

                        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                          <div className="flex items-center gap-2 text-gray-500 mb-1">
                            <ClockIcon className="w-4 h-4 text-emerald-600" />
                            <span className="text-xs font-medium">Período de Vigência</span>
                          </div>
                          <span className="text-sm font-bold text-gray-900 block">
                            {contrato.vigencia ? `${contrato.vigencia} meses` : '12 meses'}
                          </span>
                        </div>

                        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                          <div className="flex items-center gap-2 text-gray-500 mb-1">
                            <UserGroupIcon className="w-4 h-4 text-emerald-600" />
                            <span className="text-xs font-medium">Vidas Contratadas</span>
                          </div>
                          <span className="text-sm font-bold text-gray-900 block">
                            {contrato.num_colab_contrato || 0} vidas inclusas
                          </span>
                        </div>

                        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                          <div className="flex items-center gap-2 text-gray-500 mb-1">
                            <BuildingOffice2Icon className="w-4 h-4 text-emerald-600" />
                            <span className="text-xs font-medium">Origem do Contrato</span>
                          </div>
                          <span className="text-sm font-bold text-gray-900 block truncate">
                            {contrato.origem === 'grupo_economico'
                              ? `Holding: ${contrato.holding_nome || 'Grupo Econômico'}`
                              : 'Contrato Próprio da Empresa'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-10">
                      <DocumentCheckIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm font-semibold text-gray-700">Nenhum contrato ativo localizado</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Entre em contato com nossa equipe comercial para ativação do contrato de SST.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ================= ABA 3: LAUDOS REGULATÓRIOS ================= */}
            {activeTab === 'laudos' && (
              <div className="space-y-6">
                {/* Seletor de Unidades Operacionais / Postos de Trabalho (Facilities) */}
                {temPostos && (
                  <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <BuildingOffice2Icon className="w-5 h-5 text-indigo-600 shrink-0" />
                      <div>
                        <span className="text-xs font-bold text-gray-900 block">
                          Unidade / Estabelecimento Operacional
                        </span>
                        <p className="text-[11px] text-gray-500">
                          Selecione para visualizar os laudos regulatórios da sede ou de postos específicos de clientes tomadores.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 flex-wrap">
                      <button
                        type="button"
                        onClick={() => setUnidadeSelecionadaId('principal')}
                        className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                          unidadeSelecionadaId === 'principal'
                            ? 'bg-indigo-600 text-white shadow-xs'
                            : 'bg-slate-100 text-gray-700 hover:bg-slate-200'
                        }`}
                      >
                        🏢 Matriz / Sede Principal
                      </button>
                      {unidadesPostos.map((posto) => (
                        <button
                          key={posto.id}
                          type="button"
                          onClick={() => setUnidadeSelecionadaId(posto.id)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                            unidadeSelecionadaId === posto.id
                              ? 'bg-indigo-600 text-white shadow-xs'
                              : 'bg-slate-100 text-gray-700 hover:bg-slate-200'
                          }`}
                        >
                          🏭 {posto.nome}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cards dos Laudos Vigentes Principais */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {(['pgr', 'pcmso', 'ltcat'] as TipoLaudoRegulatorio[]).map((tipo) => {
                    const laudoAtivo = laudosFiltrados.find((l) => l.tipo === tipo);
                    const isDisponivel = !!laudoAtivo?.arquivo_url;

                    return (
                      <div
                        key={tipo}
                        className="bg-white border border-gray-200 rounded-2xl p-5 shadow-xs flex flex-col justify-between space-y-4"
                      >
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-emerald-700 uppercase bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-100">
                              {tipo.toUpperCase()}
                            </span>
                            {laudoAtivo?.status === 'vigente' || laudoAtivo?.status === 'concluido' ? (
                              <span className="text-[11px] bg-emerald-100 text-emerald-800 font-semibold px-2 py-0.5 rounded-full">
                                Vigente
                              </span>
                            ) : (
                              <span className="text-[11px] bg-amber-100 text-amber-800 font-semibold px-2 py-0.5 rounded-full">
                                Em Elaboração
                              </span>
                            )}
                          </div>

                          <h3 className="text-sm font-bold text-gray-900 leading-snug">
                            {getLaudoNomeCompleto(tipo)}
                          </h3>

                          <div className="pt-2 text-xs space-y-1 text-gray-600">
                            <p>
                              <strong>Ano Vigente:</strong> {laudoAtivo?.ano_vigencia || new Date().getFullYear()} (v{laudoAtivo?.versao || '1.0'})
                            </p>
                            <p>
                              <strong>Validade Até:</strong> {formatDateBR(laudoAtivo?.data_vigencia_fim)}
                            </p>
                            {laudoAtivo?.medico_coordenador && (
                              <p className="truncate">
                                <strong>Médico:</strong> {laudoAtivo.medico_coordenador}
                              </p>
                            )}
                            {laudoAtivo?.engenheiro_responsavel && (
                              <p className="truncate">
                                <strong>Engenheiro:</strong> {laudoAtivo.engenheiro_responsavel}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Botão de Download */}
                        <div>
                          {isDisponivel ? (
                            <button
                              type="button"
                              onClick={() => handleDownloadDocumento(laudoAtivo!.arquivo_url!, laudoAtivo!.arquivo_nome || 'Laudo.pdf')}
                              disabled={downloadingPath === laudoAtivo?.arquivo_url}
                              className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold shadow-xs transition-colors cursor-pointer"
                            >
                              <ArrowDownTrayIcon className="w-4 h-4" />
                              {downloadingPath === laudoAtivo?.arquivo_url ? 'Baixando...' : 'Baixar Laudo (PDF)'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled
                              className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-3 bg-slate-100 text-slate-400 border border-slate-200 rounded-xl text-xs font-semibold cursor-not-allowed"
                              title="Laudo técnico em fase de elaboração pela engenharia/medicina do trabalho"
                            >
                              <ClockIcon className="w-4 h-4" />
                              Em Elaboração / Indisponível
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Histórico Permanente de Laudos Regulatórios (NUNCA DELETA) */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-xs space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                        <DocumentDuplicateIcon className="w-4 h-4 text-emerald-600" />
                        Histórico Geral de Laudos & Versões Anteriores (Acervo Fiscal)
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Conforme preceitua a legislação trabalhista, o acervo de laudos é mantido integralmente para fins de fiscalização e auditoria.
                      </p>
                    </div>
                  </div>

                  {laudosHistorico.length === 0 ? (
                    <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl text-center text-xs text-gray-500">
                      Nenhuma versão histórica anterior registrada para esta empresa.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs text-left">
                        <thead className="bg-slate-50 text-gray-600 font-semibold border-b border-gray-200">
                          <tr>
                            <th className="py-2.5 px-3">Programa / Laudo</th>
                            {temPostos && <th className="py-2.5 px-3">Unidade / Posto</th>}
                            <th className="py-2.5 px-3">Ano / Exercício</th>
                            <th className="py-2.5 px-3">Versão</th>
                            <th className="py-2.5 px-3">Período de Vigência</th>
                            <th className="py-2.5 px-3">Data Finalização</th>
                            <th className="py-2.5 px-3 text-right">Download</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {laudosHistorico.map((item) => (
                            <tr key={item.id} className="hover:bg-slate-50/50">
                              <td className="py-3 px-3 font-semibold text-gray-900">
                                {getLaudoNomeCompleto(item.tipo)}
                              </td>
                              {temPostos && (
                                <td className="py-3 px-3 text-gray-600">
                                  {item.is_posto_servico ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                                      🏭 {item.nome_unidade_negocio || 'Posto'}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700">
                                      🏢 Sede
                                    </span>
                                  )}
                                </td>
                              )}
                              <td className="py-3 px-3 text-gray-700">{item.ano_vigencia}</td>
                              <td className="py-3 px-3 text-gray-600">v{item.versao}.0</td>
                              <td className="py-3 px-3 text-gray-600">
                                {formatDateBR(item.data_vigencia_inicio)} a {formatDateBR(item.data_vigencia_fim)}
                              </td>
                              <td className="py-3 px-3 text-gray-500">{formatDateBR(item.data_finalizacao)}</td>
                              <td className="py-3 px-3 text-right">
                                {item.arquivo_url ? (
                                  <button
                                    type="button"
                                    onClick={() => handleDownloadDocumento(item.arquivo_url, item.arquivo_nome || 'Laudo.pdf')}
                                    disabled={downloadingPath === item.arquivo_url}
                                    className="text-emerald-700 hover:text-emerald-800 font-semibold hover:underline cursor-pointer"
                                  >
                                    {downloadingPath === item.arquivo_url ? 'Baixando...' : 'Baixar PDF'}
                                  </button>
                                ) : (
                                  <span className="text-gray-400">Indisponível</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* MODAL DE RENOVAÇÃO DA PROCURAÇÃO ELETRÔNICA */}
      {showRenewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 space-y-5 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="w-5 h-5 text-emerald-600" />
                <h3 className="text-sm font-bold text-gray-900">
                  Renovar Procuração Eletrônica e-CAC
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowRenewModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <p className="text-gray-600 leading-relaxed">
                Faça o upload do novo comprovante de procuração eletrônica emitido pelo e-CAC da Receita Federal e selecione o prazo de vigência outorgado.
              </p>

              {/* Seletor de Vigência */}
              <div className="space-y-1.5">
                <label className="font-semibold text-gray-700 block">
                  Prazo de Vigência da Outorga <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { meses: 12, label: '12 Meses (1 Ano)' },
                    { meses: 24, label: '24 Meses (RFB Padrão)' },
                    { meses: 36, label: '36 Meses (3 Anos)' },
                  ].map((opt) => (
                    <button
                      key={opt.meses}
                      type="button"
                      onClick={() => setVigenciaMeses(opt.meses)}
                      className={`p-2.5 rounded-xl border text-center transition-all ${
                        vigenciaMeses === opt.meses
                          ? 'border-emerald-600 bg-emerald-50 text-emerald-800 font-bold ring-1 ring-emerald-500'
                          : 'border-gray-200 bg-white text-gray-700 hover:bg-slate-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dropzone de Upload de Arquivo */}
              <div className="space-y-1.5">
                <label className="font-semibold text-gray-700 block">
                  Arquivo da Nova Procuração (PDF ou Imagem) <span className="text-red-500">*</span>
                </label>
                <label className="flex flex-col items-center justify-center p-5 border-2 border-dashed border-gray-300 hover:border-emerald-500 rounded-xl cursor-pointer bg-slate-50 hover:bg-white transition-colors">
                  <ArrowUpTrayIcon className="w-6 h-6 text-gray-400 mb-1" />
                  <span className="font-medium text-gray-700">
                    {selectedFile ? selectedFile.name : 'Clique para selecionar o PDF da Procuração'}
                  </span>
                  <span className="text-[10px] text-gray-400 mt-0.5">
                    Envio direto e seguro para o Google Cloud Storage
                  </span>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.png"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setSelectedFile(file);
                    }}
                  />
                </label>
              </div>

              {/* Barra de Progresso durante upload */}
              {renewMutation.isPending && (
                <div className="space-y-1 py-1">
                  <div className="flex justify-between text-[11px] text-emerald-700 font-semibold">
                    <span>Transmitindo para o Storage...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Rodapé do Modal */}
            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShowRenewModal(false)}
                disabled={renewMutation.isPending}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-semibold transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => renewMutation.mutate()}
                disabled={!selectedFile || renewMutation.isPending}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold shadow-xs disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors"
              >
                {renewMutation.isPending ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Confirmar e Atualizar Vigência'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container para notificações */}
      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
    </div>
  );
}
